import type { TFunction } from 'i18next';
import { Download } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { RowAction } from '@/components/ui/row-actions-menu';

/** Which message an export that failed gets: the row limit has its own. */
function failureOf(error: unknown): 'limit' | 'other' {
  return typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'EXPORT_ROW_LIMIT_EXCEEDED'
    ? 'limit'
    : 'other';
}

/**
 * The two formats' names (decision 223): the Excel file of the view is «Esporta vista», so it is not
 * confused with the nexpense-format file beside it; the CSV keeps «Esporta CSV».
 */
function formatLabel(t: TFunction<'common'>, format: 'csv' | 'xlsx'): string {
  return format === 'xlsx' ? t('list.exportView') : t('list.export', { format: 'CSV' });
}

/**
 * The CSV and XLSX exports as «···» menu items, for the phone. The menu closes when one is picked,
 * so a failure is said in a toast, not in the button row the wide screen has.
 */
export function useListExportActions(
  onExport: (format: 'csv' | 'xlsx') => Promise<void>,
): readonly RowAction[] {
  const { t } = useTranslation('common');
  return (['csv', 'xlsx'] as const).map((format) => ({
    id: `export-${format}`,
    label: formatLabel(t, format),
    icon: Download,
    onSelect: () => {
      onExport(format).catch((error: unknown) => {
        toast.error(t(failureOf(error) === 'limit' ? 'list.exportTooLarge' : 'list.exportError'));
      });
    },
  }));
}

/**
 * The view's exports on the wide screen, «Esporta CSV» and «Esporta vista». `scope` says what the
 * file holds (#952 M5: which rows, which columns), as the buttons' description and tooltip.
 */
export function ListExportButton({
  onExport,
  scope,
}: {
  readonly onExport: (format: 'csv' | 'xlsx') => Promise<void>;
  readonly scope?: string | undefined;
}) {
  const { t } = useTranslation('common');
  const scopeId = useId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<'limit' | 'other' | null>(null);
  async function run(format: 'csv' | 'xlsx') {
    setBusy(true);
    setFailed(null);
    try {
      await onExport(format);
    } catch (error) {
      setFailed(failureOf(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2" aria-busy={busy}>
      {scope === undefined ? null : (
        <span id={scopeId} className="sr-only">
          {scope}
        </span>
      )}
      {(['csv', 'xlsx'] as const).map((format) => (
        <Button
          key={format}
          variant="outline"
          disabled={busy}
          title={scope}
          aria-describedby={scope === undefined ? undefined : scopeId}
          onClick={() => void run(format)}
        >
          <Download />
          {formatLabel(t, format)}
        </Button>
      ))}
      {failed ? (
        <span role="alert" className="text-destructive text-sm">
          {t(failed === 'limit' ? 'list.exportTooLarge' : 'list.exportError')}
        </span>
      ) : null}
    </span>
  );
}
